import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { hasOmniCardContent, type OmniArtifact } from "@/shared/selectOmniArtifacts.ts";

/**
 * The artifact the user opened from the feed, for the omnibox card to show.
 *
 * Opening an artifact presents it on the canvas, and the card below the
 * composer should be describing whatever the canvas is presenting. The card is
 * owned by `useOmniReview` inside `MessageList`, while the chips that open an
 * artifact are scattered through the feed beneath it — so the click publishes
 * here and the review reads it, the same arrangement (and the same reason) as
 * `artifactReviewQueue`.
 *
 * Deliberately not derived from the canvas stack top, which would need no
 * channel at all: `thread_canvas:update` lets the agent present a card
 * server-side, and that must not be able to move the user's review out from
 * under them. This atom carries user clicks and nothing else.
 *
 * A plain jotai atom — client-side, non-persisted, readable across two sibling
 * subtrees without a shared provider.
 */
const browsedArtifactAtom = atom<{ threadId: number; artifact: OmniArtifact } | undefined>(
  undefined,
);

/** Show an artifact in its thread's omnibox card, or clear it with `undefined`. */
export function useSetBrowsedArtifact() {
  return useSetAtom(browsedArtifactAtom);
}

/**
 * Retire the browsed card for one artifact — the counterpart to
 * {@link useSetBrowsedArtifact} for the surfaces that *settle* a card rather
 * than open one: answering its form on the canvas or in a lightbox, or firing
 * its action there.
 *
 * A browsed card is the snapshot taken when it was opened, and nothing re-reads
 * it from the refreshed feed — so a form answered anywhere but the omnibox left
 * the composer still asking a question that was already answered, with no way
 * back but Escape (BIG-7551). The review needs no such call: it re-derives its
 * cards from `items`, and {@link selectOmniCards} already releases a browsed
 * card the review holds.
 *
 * Addressed by artifact id, not by clearing whatever is there. These callers
 * fire after a round-trip, by which time the surface may be showing a card the
 * user opened in the meantime — the same rule `useOmniReview`'s synchronous
 * `releaseBrowsed` states for itself. The id alone is the identity: it is
 * unique, and the canvas can be driving an artifact from a thread other than
 * the one whose omnibox is holding it.
 */
export function useReleaseBrowsedArtifact() {
  const setBrowsed = useSetAtom(browsedArtifactAtom);
  return useCallback(
    (artifactId: string) =>
      setBrowsed((prev) => (prev?.artifact.id === artifactId ? undefined : prev)),
    [setBrowsed],
  );
}

/**
 * The artifact the user opened from **this** thread's feed, if any.
 *
 * The atom is one global, but the surfaces reading it are per-thread and outlive
 * a navigation, so a card opened in one thread would otherwise still be sitting
 * there when the next thread's omnibox reads it — presenting a stranger's
 * artifact, and submitting its decision to a thread that does not own it. The
 * ownership check lives here rather than at the call site so a reader cannot
 * forget to make it.
 */
export function useBrowsedArtifact(threadId: number) {
  const browsed = useAtomValue(browsedArtifactAtom);
  return browsed?.threadId === threadId ? browsed.artifact : undefined;
}

/**
 * What the omnibox surface shows, given the review and the artifact the user
 * opened from the feed: `cards` is the set on screen, `isBrowsing` says a
 * browsed card has the surface with the review hidden behind it, and `selectId`
 * names a card to select in the review instead of overriding it.
 *
 * An opened artifact the review already holds is reached by *selecting* it
 * there, never by overriding: that keeps the pager, the staged notes and the
 * decided count intact, and it beats the position a previous arrow press left
 * behind. One the review has no card for — anything outside the current turn —
 * takes the surface over instead, alone, so it behaves like the lone artifact it
 * is rather than borrowing the hidden review's shape.
 *
 * An artifact with nothing to draw takes nothing over. The feed opens settled
 * artifacts as readily as pending ones, and a settled one carries no biscuit to
 * render (see {@link hasOmniCardContent}) — so letting it claim the surface
 * emptied the composer instead of filling it (BIG-7439). Its canvas still
 * opens; only its claim on the omnibox is refused.
 */
export function selectOmniCards({
  review,
  browsed,
}: {
  review: readonly OmniArtifact[];
  browsed: OmniArtifact | undefined;
}) {
  if (!browsed) return { cards: review, isBrowsing: false, selectId: undefined };
  if (review.some((a) => a.id === browsed.id)) {
    return { cards: review, isBrowsing: false, selectId: browsed.id };
  }
  if (!hasOmniCardContent(browsed)) {
    return { cards: review, isBrowsing: false, selectId: undefined };
  }
  return { cards: [browsed], isBrowsing: true, selectId: undefined };
}
